import { useCallback } from 'react';
import { css } from '@emotion/react';
import { BulkNotificationHistoryActions, NotificationFilters, NotificationTray } from '@frontend/notifications';
import { PanelPortal, PanelHeader, PanelContent } from '@frontend/panel-engine';
import { useScopedAppFlagStore } from '@frontend/scope';
import { Heading } from '@frontend/design-system';
import { PaddedContent } from '../../layout/padded-content';
import { useTopBarShallowStore } from '../../store/use-top-bar-store';
import { labelMap } from '../../utils/label-map';

interface NotificationsPanelProps {
  onClose: () => void;
  onLocationTrayOpen: (e: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => void;
  showNotificationFilterAsChips?: boolean;
  showForYouNotificationsTab?: boolean;
}

export const NotificationsPanel = ({
  onClose,
  onLocationTrayOpen,
  showNotificationFilterAsChips,
  showForYouNotificationsTab,
}: NotificationsPanelProps) => (
  <PanelPortal enableAnimations id='notifications' width='medium' onClose={onClose}>
    <PaddedContent css={{ paddingBottom: 0 }}>
      <header
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <Heading
          level={2}
          css={{ marginBottom: 0, display: 'flex', gap: 8, alignItems: 'center' }}
          data-testid='tray-header'
        >
          {labelMap['notifications']}
        </Heading>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <BulkNotificationHistoryActions
            hideFilterIcon={showNotificationFilterAsChips || showForYouNotificationsTab}
          />
          <PanelHeader.Close iconName='x' size='large' />
        </div>
      </header>
      {showNotificationFilterAsChips && !showForYouNotificationsTab && <NotificationFilters />}
    </PaddedContent>
    <PanelContent css={{ padding: 0 }}>
      <NotificationTray closeModal={onClose} selectLocationTray={onLocationTrayOpen} />
    </PanelContent>
  </PanelPortal>
);

export const NotificationsPanelWrapper = () => {
  const { setNotificationsOpen, isNotificationsOpen, handleSelectableIconClick } = useTopBarShallowStore(
    'setNotificationsOpen',
    'isNotificationsOpen',
    'handleSelectableIconClick'
  );
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');
  const showNotificationFilterAsChips = getFeatureFlagValue('notification-filter-chips');

  const handleClose = useCallback(() => {
    setNotificationsOpen(false);
  }, [setNotificationsOpen]);

  return (
    isNotificationsOpen && (
      <NotificationsPanel
        onClose={handleClose}
        onLocationTrayOpen={(e) => {
          handleSelectableIconClick('location', e);
        }}
        showNotificationFilterAsChips={showNotificationFilterAsChips}
        showForYouNotificationsTab={showForYouNotificationsTab}
      />
    )
  );
};
